'use client';

import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface Donation {
  id: string;
  donor_name: string | null;
  amount: number;
  cause_category: string | null;
  created_at: string;
}

const CAUSE_COLORS: Record<string, string> = {
  education: 'bg-blue-100 text-blue-700',
  health: 'bg-green-100 text-green-700',
  food: 'bg-yellow-100 text-yellow-700',
  shelter: 'bg-orange-100 text-orange-700',
  water: 'bg-cyan-100 text-cyan-700',
  emergency: 'bg-red-100 text-red-700',
  general: 'bg-purple-100 text-purple-700',
};

function getCauseColor(cause: string | null): string {
  if (!cause) return 'bg-purple-100 text-purple-700';
  const key = cause.toLowerCase();
  return CAUSE_COLORS[key] ?? 'bg-purple-100 text-purple-700';
}

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function timeAgo(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function LiveDonationFeed() {
  const [donations, setDonations] = useState<Donation[]>([]);
  const [loading, setLoading] = useState(true);
  const [newIds, setNewIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const supabase = createClient();

    // Initial fetch — last 10 completed donations
    supabase
      .from('donations')
      .select('id, donor_name, amount, cause_category, created_at')
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(10)
      .then(({ data }) => {
        if (data) setDonations(data as Donation[]);
        setLoading(false);
      });

    // Real-time subscription for new completed donations
    const channel = supabase
      .channel('live_donations_feed')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'donations',
        },
        (payload) => {
          const newDonation = payload.new as Donation;
          if (newDonation.status !== 'completed') return;
          setDonations((prev) => [newDonation, ...prev].slice(0, 10));
          setNewIds((prev) => new Set(prev).add(newDonation.id));
          setTimeout(() => {
            setNewIds((prev) => {
              const next = new Set(prev);
              next.delete(newDonation.id);
              return next;
            });
          }, 3000);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'donations',
        },
        (payload) => {
          const updated = payload.new as Donation;
          if (updated.status !== 'completed') return;
          setDonations((prev) => {
            const exists = prev.some((d) => d.id === updated.id);
            if (exists) {
              return prev.map((d) => (d.id === updated.id ? updated : d));
            }
            const next = [updated, ...prev].slice(0, 10);
            setNewIds((ids) => new Set(ids).add(updated.id));
            setTimeout(() => {
              setNewIds((ids) => {
                const n = new Set(ids);
                n.delete(updated.id);
                return n;
              });
            }, 3000);
            return next;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-600"></span>
            </span>
            Live Donations
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Community in Action</h2>
          <p className="mt-2 text-gray-500 text-base">
            Real-time donations from our generous supporters around the world.
          </p>
        </div>

        {/* Feed */}
        <div className="space-y-3">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 animate-pulse flex items-center gap-4"
              >
                <div className="h-10 w-10 rounded-full bg-gray-200 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 bg-gray-200 rounded w-1/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
                <div className="h-5 w-16 bg-gray-200 rounded-full" />
              </div>
            ))
          ) : donations.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <svg className="mx-auto h-12 w-12 mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <p className="text-sm">No donations yet. Be the first to give!</p>
            </div>
          ) : (
            donations.map((donation) => {
              const isNew = newIds.has(donation.id);
              const initials = donation.donor_name
                ? donation.donor_name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
                : 'AN';
              const displayName = donation.donor_name || 'Anonymous';
              const cause = donation.cause_category
                ? donation.cause_category.charAt(0).toUpperCase() + donation.cause_category.slice(1)
                : 'General';

              return (
                <div
                  key={donation.id}
                  className={`bg-white rounded-xl px-5 py-4 shadow-sm border transition-all duration-500 flex items-center gap-4 ${
                    isNew
                      ? 'border-green-300 ring-2 ring-green-100 scale-[1.01]'
                      : 'border-gray-100'
                  }`}
                >
                  {/* Avatar */}
                  <div className="shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                    {initials}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {displayName}
                      {isNew && (
                        <span className="ml-2 inline-flex items-center text-xs font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                          New
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      donated to{' '}
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${getCauseColor(donation.cause_category)}`}>
                        {cause}
                      </span>
                      <span className="ml-1.5">{timeAgo(donation.created_at)}</span>
                    </p>
                  </div>

                  {/* Amount */}
                  <div className="shrink-0 text-right">
                    <span className="text-base font-bold text-gray-900">
                      {formatAmount(donation.amount)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* CTA */}
        {!loading && donations.length > 0 && (
          <div className="mt-8 text-center">
            <a
              href="/donation-screen"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-full transition-colors shadow-md"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Join the movement — Donate now
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
